import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChartBar, FacebookLogo, WhatsappLogo, Heart, ShareNetwork, TrendUp, MapPin, Wrench, CalendarBlank } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/analytics`, { withCredentials: true });
      setData(d);
    } catch (error) { console.error("Analytics fetch error:", error.message); toast.error('Erreur chargement analytics'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) return (
    <div className="flex items-center justify-center py-16" data-testid="analytics-loading">
      <div className="animate-spin w-6 h-6 border-2 border-[#3F3F46] border-t-[#8B5CF6] rounded-full" />
    </div>
  );

  if (!data) return null;

  const maxWeekly = Math.max(...data.weekly_activity.map(w => w.count), 1);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="analytics-panel">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4">
          <TrendUp size={18} weight="duotone" className="text-[#A78BFA] mb-2" />
          <p className="text-2xl font-black text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{data.total_posts}</p>
          <p className="text-xs text-[#71717A]">Posts générés</p>
        </div>
        <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4">
          <CalendarBlank size={18} weight="duotone" className="text-[#A78BFA] mb-2" />
          <p className="text-2xl font-black text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{data.posts_this_month}</p>
          <p className="text-xs text-[#71717A]">Ce mois-ci</p>
        </div>
        <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4">
          <ShareNetwork size={18} weight="duotone" className="text-[#A78BFA] mb-2" />
          <p className="text-2xl font-black text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{data.gallery_posts}</p>
          <p className="text-xs text-[#71717A]">En galerie</p>
        </div>
        <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4">
          <Heart size={18} weight="duotone" className="text-[#A78BFA] mb-2" />
          <p className="text-2xl font-black text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{data.total_likes}</p>
          <p className="text-xs text-[#71717A]">Likes reçus</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Weekly Activity */}
        <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-5">
          <h3 className="font-bold text-sm text-[#F4F4F5] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            <ChartBar size={16} weight="duotone" className="inline mr-1.5 text-[#A78BFA]" />
            Activité (4 dernières semaines)
          </h3>
          <div className="flex items-end gap-3 h-28">
            {data.weekly_activity.map((w) => (
              <div key={`week-${w.week}`} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs font-bold text-[#F4F4F5]">{w.count}</span>
                <div className="w-full rounded-t-md bg-[#8B5CF6] transition-all" style={{ height: `${Math.max(8, (w.count / maxWeekly) * 80)}px` }} />
                <span className="text-[10px] text-[#71717A]">{w.week}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Platform Split */}
        <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-5">
          <h3 className="font-bold text-sm text-[#F4F4F5] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Plateformes</h3>
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-[#E4E4E7] flex items-center gap-1.5"><FacebookLogo size={16} weight="duotone" className="text-blue-400" /> Facebook</span>
                <span className="text-sm font-bold text-[#F4F4F5]">{data.platforms.facebook}</span>
              </div>
              <div className="w-full h-2 bg-[#27272A] rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${data.total_posts ? (data.platforms.facebook / data.total_posts) * 100 : 0}%` }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-[#E4E4E7] flex items-center gap-1.5"><WhatsappLogo size={16} weight="duotone" className="text-green-400" /> WhatsApp</span>
                <span className="text-sm font-bold text-[#F4F4F5]">{data.platforms.whatsapp}</span>
              </div>
              <div className="w-full h-2 bg-[#27272A] rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${data.total_posts ? (data.platforms.whatsapp / data.total_posts) * 100 : 0}%` }} />
              </div>
            </div>
          </div>
        </div>

        {/* Top Types */}
        <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-5">
          <h3 className="font-bold text-sm text-[#F4F4F5] mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            <Wrench size={16} weight="duotone" className="inline mr-1.5 text-[#A78BFA]" />
            Types de travaux
          </h3>
          {data.top_types.length === 0 ? (
            <p className="text-sm text-[#71717A]">Aucune donnée</p>
          ) : (
            <div className="space-y-2">
              {data.top_types.map((t) => (
                <div key={`type-${t.name}`} className="flex items-center justify-between">
                  <span className="text-sm text-[#E4E4E7]">{t.name}</span>
                  <span className="text-xs font-bold text-[#A78BFA] bg-[#8B5CF6]/10 px-2 py-0.5 rounded">{t.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Cities */}
        <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-5">
          <h3 className="font-bold text-sm text-[#F4F4F5] mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            <MapPin size={16} weight="duotone" className="inline mr-1.5 text-[#A78BFA]" />
            Villes ciblées
          </h3>
          {data.top_cities.length === 0 ? (
            <p className="text-sm text-[#71717A]">Aucune donnée</p>
          ) : (
            <div className="space-y-2">
              {data.top_cities.map((c) => (
                <div key={`city-${c.name}`} className="flex items-center justify-between">
                  <span className="text-sm text-[#E4E4E7]">{c.name}</span>
                  <span className="text-xs font-bold text-[#A78BFA] bg-[#8B5CF6]/10 px-2 py-0.5 rounded">{c.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
